import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Divider,
  Alert
} from '@mui/material';
import { LockOutlined } from '@mui/icons-material';

interface TwoFactorVerificationProps {
  userId: string;
  onVerify: (userId: string, verified: boolean) => void;
  onCancel: () => void;
}

const TwoFactorVerification: React.FC<TwoFactorVerificationProps> = ({
  userId,
  onVerify,
  onCancel
}) => {
  const [token, setToken] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useBackupCode, setUseBackupCode] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch('/api/auth/complete-2fa-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId,
          token: useBackupCode ? '' : token,
          backupCode: useBackupCode ? backupCode : ''
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Verification failed');
      }

      // If verification is successful, call the onVerify callback
      onVerify(userId, data.verified);
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 400, mx: 'auto', mt: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
        <LockOutlined sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
        <Typography variant="h5" component="h1" gutterBottom>
          Two-Factor Authentication
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          Please enter the verification code from your authenticator app
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {!useBackupCode ? (
          <TextField
            fullWidth
            label="Verification Code"
            variant="outlined"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Enter 6-digit code"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
            required
            autoFocus
            sx={{ mb: 3 }}
          />
        ) : (
          <TextField
            fullWidth
            label="Backup Code"
            variant="outlined"
            value={backupCode}
            onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
            placeholder="Enter backup code"
            required
            autoFocus
            sx={{ mb: 3 }}
          />
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{ mb: 2, py: 1.5 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Verify'}
        </Button>

        <Button
          type="button"
          fullWidth
          variant="outlined"
          onClick={onCancel}
          sx={{ mb: 2 }}
        >
          Cancel
        </Button>
      </form>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ textAlign: 'center' }}>
        <Button
          color="secondary"
          onClick={() => setUseBackupCode(!useBackupCode)}
          sx={{ textTransform: 'none' }}
        >
          {useBackupCode
            ? 'Use authentication code instead'
            : 'Use backup code instead'}
        </Button>
      </Box>
    </Paper>
  );
};

export default TwoFactorVerification; 